import React, { useEffect, useState, useRef } from "react";
import { Check, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getAddresses,
  updateAddress,
  createAddress,
  clearError,
  deleteAddress,
} from "../../store/slices/addressesSlice";
import {
  PartnerInfoWithClientCode,
  Partner,
  UserFilters as Filters,
  UpdatePartnerInfoWithClientCode,
} from "../../types";
import { withVersion } from "../../utils/concurrency";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { PartnerForm } from "../../components/partners/PartnerForm";
import { PartnerTable } from "../../components/partners/PartnerTable";
import { Pagination } from "../../components/ListView/Pagination";
import { Input } from "../../components/UI/FormField";

export const AddressesListing: React.FC = () => {
  const { t } = useTranslation(["addresses", "partners", "common"]);
  const dispatch = useAppDispatch();
  const {
    addresses,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.addresses);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Partner | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open create modal when navigated with ?new=true (e.g. from dashboard quick action)
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setEditingAddress(null);
      setIsModalOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    page: 1,
    limit: 20,
    status: "",
  });

  const prevSearch = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
      status: filters.status,
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getAddresses(params));
      } else {
        const t = setTimeout(() => dispatch(getAddresses(params)), 750);
        return () => clearTimeout(t);
      }
      return;
    }
    dispatch(getAddresses(params));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(addresses.map((address) => address._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const handleCreateAddress = async (data: PartnerInfoWithClientCode) => {
    try {
      await dispatch(createAddress(data)).unwrap();
      toast.success(t("addresses:messages.createdSuccess"));
      setIsModalOpen(false);
    } catch (error) {}
  };

  const handleUpdateAddress = async (data: Partial<Partner>) => {
    if (!editingAddress) return;

    try {
      if (!data.companyName || !data.address) {
        toast.error(t("partners:form.companyNameAndAddressRequired"));
        return;
      }

      const addressInfo: UpdatePartnerInfoWithClientCode = {
        companyName: data.companyName,
        registerationCode: data.registerationCode,
        clientCode: data.clientCode,
        phoneNumber: data.phoneNumber,
        emailAddress: data.emailAddress,
        address: {
          street: data.address.street,
          city: data.address.city,
          state: data.address.state,
          postalCode: data.address.postalCode,
          country: data.address.country,
          // Carried through explicitly: the API requires a map location on
          // every directory write, so dropping it here fails an edit that only
          // touched an unrelated field.
          coordinates: data.address.coordinates,
        },
      };

      await dispatch(
        updateAddress({
          id: editingAddress._id,
          addressData: withVersion(addressInfo, editingAddress),
        }),
      ).unwrap();

      toast.success(t("addresses:messages.updatedSuccess"));
      setIsModalOpen(false);
      setEditingAddress(null);
    } catch (error) {
      console.error(error);
      toast.error(
        typeof error === "string" ? error : t("addresses:messages.updateFailed"),
      );
    }
  };

  const handleEdit = (address: Partner) => {
    setEditingAddress(address);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("addresses:page.deleteConfirm"))) {
      try {
        await dispatch(deleteAddress(id)).unwrap();
        toast.success(t("addresses:messages.deletedSuccess"));
        setSelectedIds((prev) =>
          prev.filter((selectedId) => selectedId !== id),
        );
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAddress(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("addresses:page.title")}
          </h1>
          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex-1 min-w-[250px] max-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t("addresses:page.search")}
                  value={filters.search}
                  onChange={(e) =>
                    handleFiltersChange({ search: e.target.value })
                  }
                  className="pl-10 bg-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("addresses:page.addAddress")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("addresses:page.itemsSelected", {
                  count: selectedIds.length,
                })}
              </span>
            </div>
            <button className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" />
              <span>{t("addresses:page.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <PartnerTable
            partners={addresses}
            // `vatNumber` is a partner field; an address record has no such
            // thing, so the column could only ever be blank here.
            withVat={false}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingAddress
              ? `${t("addresses:page.idPrefix")}${editingAddress._id.slice(-6).toUpperCase()}`
              : t("addresses:page.createAddress")
          }
          size="lg"
        >
          <div className="flex-1 overflow-y-auto">
            <PartnerForm
              partner={editingAddress || undefined}
              onSubmit={(data) => {
                if (editingAddress) {
                  return handleUpdateAddress(data as any);
                } else {
                  return handleCreateAddress(data as PartnerInfoWithClientCode);
                }
              }}
              loading={loading}
              isEditMode={!!editingAddress}
              requireRegistrationCode={false}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
